<script setup lang="ts">
import { Calendar, LayoutGrid, Palette, ScrollText } from "@lucide/vue";
import { computed, inject, ref } from "vue";
import { useI18n } from "../composables/useI18n";
import { POPUP_RAIL_TAB_KEY } from "../keys";
import type { PopupRailTabId } from "../types/popup-rail-tab";
import PopupDesignView from "./PopupDesignView.vue";
import SidebarEmailRail from "./SidebarEmailRail.vue";

const fallbackRailTab = ref<PopupRailTabId>("blocks");
const popupRailTab = inject(POPUP_RAIL_TAB_KEY, fallbackRailTab);

const { t } = useI18n();

const railTabs = computed(() => [
  { id: "design" as const, icon: Palette, label: t.popupSidebar.railDesign },
  { id: "blocks" as const, icon: LayoutGrid, label: t.popupSidebar.railBlocks },
  {
    id: "displayRules" as const,
    icon: ScrollText,
    label: t.popupSidebar.railDisplayRules,
  },
  {
    id: "schedule" as const,
    icon: Calendar,
    label: t.popupSidebar.railSchedule,
  },
]);
</script>

<template>
  <div
    class="tpl-popup-left-chrome tpl:absolute tpl:top-14 tpl:bottom-0 tpl:left-0 tpl:z-40 tpl:flex tpl:overflow-hidden"
    style="
      border-right: 1px solid var(--tpl-border);
      box-shadow: var(--tpl-shadow-sm);
    "
    :aria-label="t.popupSidebar.workspaceLabel"
  >
    <nav
      class="tpl:flex tpl:w-[72px] tpl:shrink-0 tpl:flex-col tpl:items-stretch tpl:gap-0.5 tpl:overflow-y-auto tpl:py-2 tpl:px-1"
      style="
        background-color: color-mix(
          in srgb,
          var(--tpl-bg-elevated) 92%,
          transparent
        );
      "
      :aria-label="t.popupSidebar.railNavLabel"
    >
      <button
        v-for="tab in railTabs"
        :key="tab.id"
        type="button"
        class="tpl:flex tpl:flex-col tpl:items-center tpl:justify-center tpl:gap-0.5 tpl:rounded-[var(--tpl-radius-sm)] tpl:border-none tpl:py-2 tpl:px-1 tpl:text-[var(--tpl-text-muted)] tpl:transition-all tpl:duration-150 focus-visible:tpl:outline focus-visible:tpl:outline-2 focus-visible:tpl:outline-offset-[-2px] focus-visible:tpl:outline-[var(--tpl-primary)]"
        :class="
          popupRailTab === tab.id
            ? 'tpl:bg-[var(--tpl-bg)] tpl:text-[var(--tpl-primary)] tpl:shadow-sm'
            : 'tpl:bg-transparent hover:tpl:bg-[var(--tpl-bg-hover)] hover:tpl:text-[var(--tpl-text)]'
        "
        :aria-current="popupRailTab === tab.id ? 'page' : undefined"
        @click="popupRailTab = tab.id"
      >
        <component
          :is="tab.icon"
          :size="18"
          :stroke-width="1.75"
          class="tpl:shrink-0"
        />
        <span
          class="tpl:max-w-[4.25rem] tpl:text-center tpl:text-[10px] tpl:font-medium tpl:leading-tight"
        >
          {{ tab.label }}
        </span>
      </button>
    </nav>

    <SidebarEmailRail v-if="popupRailTab === 'blocks'" layout="popupAdjacent" />

    <div
      v-else-if="popupRailTab === 'design'"
      class="tpl:flex tpl:h-full tpl:w-[320px] tpl:min-h-0 tpl:shrink-0 tpl:flex-col tpl:overflow-y-auto tpl:overflow-x-hidden tpl:border-l tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)]"
      role="region"
      :aria-label="t.popupSidebar.railDesign"
    >
      <PopupDesignView layout="popupAdjacent" />
    </div>
  </div>
</template>
